import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { HeartPulse, LogOut, LayoutDashboard, Image as ImageIcon, Palette, Star, FileText, Phone, GraduationCap, Building2, Briefcase, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSettings, assetUrl } from "@/context/SettingsContext";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/admin", label: "Inquiries", icon: LayoutDashboard, end: true },
  { to: "/admin/branding", label: "Branding & Logo", icon: ImageIcon },
  { to: "/admin/theme", label: "Theme & Fonts", icon: Palette },
  { to: "/admin/hero", label: "Hero Section", icon: Star },
  { to: "/admin/content", label: "Homepage Content", icon: FileText },
  { to: "/admin/courses", label: "Courses & About", icon: GraduationCap },
  { to: "/admin/facilities", label: "Facilities & Placements", icon: Building2 },
  { to: "/admin/careers", label: "Careers Page", icon: Briefcase },
  { to: "/admin/contact", label: "Contact & Footer", icon: Phone },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const brand = settings?.branding || {};
  const logoDark = brand.logoDark ? assetUrl(brand.logoDark) : "";

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 flex-col bg-white border-r border-slate-200 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 px-6 h-20 border-b border-slate-200">
          {logoDark ? (
            <img src={logoDark} alt={brand.shortName} className="h-10 w-auto object-contain" />
          ) : (
            <span className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
              <HeartPulse className="w-4 h-4" />
            </span>
          )}
          <span className="leading-tight">
            <span className="block font-heading text-base text-slate-900 font-semibold">{brand.shortName || "Ambigara"}</span>
            <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Admin Panel</span>
          </span>
        </Link>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "text-white shadow-sm"
                    : "text-slate-700 hover:bg-slate-50"
                }`
              }
              style={({ isActive }) => (isActive ? { backgroundColor: "var(--brand-primary)" } : undefined)}
            >
              <n.icon className="w-4 h-4" />
              <span className="flex-1">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Appearance</span>
            <ThemeToggle compact dataTestid="admin-theme-toggle" />
          </div>
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-xs text-slate-500 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-50">
            <span className="flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5" /> Visit Website</span>
          </a>
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Signed in as</p>
            <p className="text-sm text-slate-900 font-medium truncate">{user?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-full border-slate-200 hover:bg-slate-50 h-10"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 h-16 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          {logoDark ? <img src={logoDark} alt={brand.shortName} className="h-8 w-auto object-contain" /> : <HeartPulse className="w-6 h-6" style={{ color: "var(--brand-primary)" }} />}
          <span className="font-heading text-sm font-semibold">Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle compact dataTestid="admin-theme-toggle-mobile" />
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-slate-600" data-testid="admin-logout-btn-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile nav — horizontal scroll */}
      <div className="lg:hidden fixed top-16 left-0 right-0 z-20 bg-white border-b border-slate-200 overflow-x-auto">
        <div className="flex gap-1 px-3 py-2 min-w-max">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                  isActive ? "text-white" : "text-slate-600 bg-slate-50"
                }`
              }
              style={({ isActive }) => (isActive ? { backgroundColor: "var(--brand-primary)" } : undefined)}
            >
              <n.icon className="w-3.5 h-3.5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 lg:pt-0 pt-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
